/**
 * The wrapper every screen in this module that clones a `pages/apr/` screen renders inside.
 *
 * Precedent: `apps/portal/web/src/pages/apr/AprScreen.tsx` — the TECHNIQUE is cloned, the
 * file is not imported and `.apr-app` is not used (DECISIONS.md D-003; UI_CONVENTIONS §2.2:
 * "`AprScreen` is in an APR product folder, so the `.apr-app` wrapper does not apply here
 * and must not be imported").
 *
 * WHY A WRAPPER AT ALL. The screens cloned from `pages/apr/` keep the source's markup and
 * class names — `.page`, `.btn`, `.form-modal`, `.form-group`, `.rd-page-title` — and those
 * rules have to come from somewhere. `source-vocabulary.css` carries the ones this module
 * uses, each cited to its file and line, and every selector in it is scoped to `.erp-app`.
 * That buys the same guarantee `AprScreen.tsx:11-23` was written for, in both directions:
 * this module's copies of `.btn` and `.page` cannot reach the platform's MUI screens or
 * APR's, and APR's cannot reach these.
 *
 * WHICH SCREENS USE IT — and it is not all of them. APR is two codebases wearing one name
 * (UI_CONVENTIONS §0.1), and SCREEN_MAP names the half each screen clones:
 *
 *   · Initiative list (#1 — Framework Management), Initiative detail (#2 — the APR Model
 *     panel), Capability model editor (#5 — Framework Builder) clone the `pages/apr/` half,
 *     so they render inside this wrapper.
 *   · The Business Unit hierarchy editor has NO APR precedent at all (#4 is a Gap) and
 *     UI_GAPS §4 Option B builds it from `ScrollTable` and `SelectField` — the platform's
 *     own shared layer, imported normally. It therefore does NOT render inside this wrapper,
 *     because mixing the two vocabularies in one screen is the mistake §18.3 names.
 *
 * There is no `source-css/` here and nothing to regenerate: this module has no source app to
 * clone from, so every rule it uses was copied by hand from a cited line and lives in one
 * 300-line stylesheet rather than 10,935 lines of unscoped CSS.
 */
import type { ReactNode } from 'react';
import './chrome/panelWidths.css';
import './source-vocabulary.css';

export function ErpScreen({ children }: { children: ReactNode }) {
  return <div className="erp-app">{children}</div>;
}
